import { useMemo, useState } from "react"
import { observer } from "mobx-react-lite"

import { InnerCard, InnerCardWarning, ShowWhenTrue } from "@/components/index"
import { DollarIcon, WalletIcon } from "@/domains/icons"
import { cn, dollars } from "@/domains/library"
import { INSUFFICIENT_BALANCE_ERROR } from "@/features/liquidity/constants"

import { TokenStore } from "../token-store"
import { SimpleTokenDialog } from "./token-dialog/simple-token-dialog"
import { TokenInputBalance } from "./token-input-balance"
import { TokenInputSymbol } from "./token-input-symbol"
import { TokenInputValue } from "./token-input-value"
import { ITokenPercentageButtons } from "./token-percentage-buttons"
import { TokenSelectable } from "./token-selectable"
import { TokenToggleButtonsSlider } from "./token-toggle-buttons-slider"

export type TokenInputDisabled = {
  amount?: boolean
  percentageButtons?: boolean
}

type TokenInputCardProps = {
  amount: string
  disabled?: TokenInputDisabled
  disabledPercentageButtons?: boolean
  externalError?: { message: string } | null
  placeholder?: string
  showTokenSelectable?: boolean
  showTokenSymbol?: boolean
  showPercentageButtons?: boolean
  token: TokenStore | null | undefined
  tokens: Array<TokenStore>
  onTokenAmountChange: (newAmount: string) => void
  onTokenSelect?: (token: TokenStore | null) => void
}

export const TokenInputCard = observer(
  ({
    amount,
    disabled = { amount: false, percentageButtons: false },
    externalError = null,
    showTokenSelectable = false,
    showTokenSymbol = true,
    showPercentageButtons = true,
    token,
    tokens,
    onTokenAmountChange,
    onTokenSelect,
    placeholder = "0",
  }: TokenInputCardProps) => {
    const [isTokenDialogOpen, setIsTokenDialogOpen] = useState(false)

    const tokenIconSize = 28

    if (!token) {
      return null
    }
    const dollarValue = token.calculateValueUSD(Number(amount))

    // Compute slider value on the fly from the current amount
    const sliderValue = useMemo(() => {
      return token.balance > 0
        ? Math.min(100, Math.max(0, (Number(amount) / Number(token.formatUnits(token.balance))) * 100))
        : 0
    }, [amount, token.balance, token])

    // Error handling: external error takes precedence, otherwise check internal insufficient balance
    const amountWei = token.parseUnits(amount)
    const internalError = amountWei > token.balance ? INSUFFICIENT_BALANCE_ERROR : null
    const activeError = externalError !== null ? externalError.message : internalError
    const hasError = activeError !== null

    const buttonsToken: ITokenPercentageButtons = {
      symbol: token.symbol ?? "unknown",
      imageURL: token.imageURL ?? "",
      decimals: token.decimals ?? 18,
      calculateValue: (percentage: number): string => {
        return token.calculateValuePercentage(token.balance, percentage) ?? "0"
      },
    }

    return (
      <InnerCard hasWarning={hasError}>
        <div className="flex flex-row items-center justify-between gap-2">
          <div className="grow">
            <TokenInputValue
              disabled={disabled.amount}
              value={amount}
              token={token}
              onValueChange={onTokenAmountChange}
              className={cn("h-full p-0 py-2 pl-1")}
              placeholder={placeholder ?? "0"}
            />
          </div>
          <ShowWhenTrue when={showTokenSelectable}>
            <TokenSelectable token={token} setIsOpen={setIsTokenDialogOpen} size={tokenIconSize} />
            <SimpleTokenDialog
              isOpen={isTokenDialogOpen}
              tokens={tokens}
              onClose={() => {
                setIsTokenDialogOpen(false)
              }}
              onTokenSelect={(token: TokenStore | null) => {
                onTokenSelect?.(token)
                onTokenAmountChange("0")
                setIsTokenDialogOpen(false)
              }}
            />
          </ShowWhenTrue>
          <ShowWhenTrue when={showTokenSymbol}>
            <TokenInputSymbol token={token} size={tokenIconSize} />
          </ShowWhenTrue>
        </div>

        {/* balances */}
        <div className="flex w-full flex-row items-center justify-between text-xs text-t100">
          <ShowWhenTrue when={hasError === false}>
            <div className="flex items-center gap-1 text-sm ">
              <DollarIcon size={14} className="text-t200" />
              <p>~{dollars(dollarValue)}</p>
            </div>
          </ShowWhenTrue>
          <ShowWhenTrue when={hasError}>
            <InnerCardWarning warning={activeError ?? ""} />
          </ShowWhenTrue>
          <TokenInputBalance token={token} label={<WalletIcon size={14} className="text-t200" />} />
        </div>

        <ShowWhenTrue when={showPercentageButtons}>
          <TokenToggleButtonsSlider
            buttonsToken={buttonsToken}
            disabled={disabled}
            sliderValue={sliderValue}
            token={token}
            onSetSliderValue={() => {}}
            onTokenAmountChange={onTokenAmountChange}
          />
        </ShowWhenTrue>
      </InnerCard>
    )
  },
)
